/**
 * Opportunity history — WON / LOST / DELETED deals filtered by period.
 */
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Page, Card, Text, Badge, BlockStack } from "@shopify/polaris";
import DateRangePicker from "features/datepicker/date-range-picker";
import { formatPrettyDate } from "utils/helperFunctions";
import { fetchBoards } from "containers/project-boards/project-boards.service";
import ProjectBoardSwitcher from "components/project/project-board-switcher/project-board-switcher";
import {
  fetchOpportunityHistory,
  OpportunityHistoryCounts,
  OpportunityHistoryEntry,
  OpportunityOutcome,
} from "services/project-opportunity-history.service";

type OutcomeFilter = "all" | OpportunityOutcome;

const defaultCounts: OpportunityHistoryCounts = {
  all: 0,
  WON: 0,
  LOST: 0,
  DELETED: 0,
};

function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Default = current calendar month. */
function currentMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toYmd(start), end: toYmd(end) };
}

const STICKY_NAME_CELL = "sticky left-0 z-[1]";

function outcomeBadgeTone(
  outcome: OpportunityOutcome | null
): "success" | "critical" | "info" | undefined {
  if (outcome === "WON") return "success";
  if (outcome === "LOST") return "critical";
  if (outcome === "DELETED") return "info";
  return undefined;
}

function formatEur(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("lt-LT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(value);
}

export default function ProjectOpportunityHistory() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialRange = currentMonthRange();
  const [outcome, setOutcome] = useState<OutcomeFilter>(
    () => (searchParams.get("outcome") as OutcomeFilter) || "all"
  );
  const [boardId, setBoardId] = useState(
    () => searchParams.get("boardId") || ""
  );
  const [dateRange, setDateRange] = useState({
    start: searchParams.get("df") || initialRange.start,
    end: searchParams.get("dt") || initialRange.end,
  });
  const [applied, setApplied] = useState({
    outcome: (searchParams.get("outcome") as OutcomeFilter) || "all",
    boardId: searchParams.get("boardId") || "",
    start: searchParams.get("df") || initialRange.start,
    end: searchParams.get("dt") || initialRange.end,
  });
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState<OpportunityHistoryEntry[]>([]);
  const [counts, setCounts] = useState<OpportunityHistoryCounts>(defaultCounts);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [boards, setBoards] = useState<Array<{ id: string; name: string }>>(
    []
  );

  useEffect(() => {
    if (!companyId) return;
    fetchBoards(companyId, { page: 1, pageSize: 100 })
      .then((data) => {
        const list = Array.isArray(data?.collection) ? data.collection : [];
        setBoards(
          list.map((b) => ({
            id: b.id,
            name: b.name || "Board",
          }))
        );
      })
      .catch(() => setBoards([]));
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await fetchOpportunityHistory(companyId, {
        outcome: applied.outcome,
        dateFrom: applied.start || undefined,
        dateTo: applied.end || undefined,
        boardId: applied.boardId || undefined,
        page,
        pageSize: 50,
      });
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setCounts({ ...defaultCounts, ...(data.counts || {}) });
      setTotalPages(data.pagination?.totalPages || 1);
    } catch {
      toast.error(
        t("project_opportunity_history_load_failed", {
          defaultValue: "Could not load opportunity history.",
        })
      );
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, applied, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  const syncUrl = (next: typeof applied) => {
    const params = new URLSearchParams();
    if (next.outcome && next.outcome !== "all") {
      params.set("outcome", next.outcome);
    }
    if (next.boardId) params.set("boardId", next.boardId);
    if (next.start) params.set("df", next.start);
    if (next.end) params.set("dt", next.end);
    setSearchParams(params, { replace: true });
  };

  const applyFilters = (e?: FormEvent) => {
    e?.preventDefault();
    const next = {
      outcome,
      boardId,
      start: dateRange.start,
      end: dateRange.end,
    };
    setPage(1);
    setApplied(next);
    syncUrl(next);
  };

  const clearFilters = () => {
    const range = currentMonthRange();
    setOutcome("all");
    setBoardId("");
    setDateRange(range);
    const next = {
      outcome: "all" as OutcomeFilter,
      boardId: "",
      start: range.start,
      end: range.end,
    };
    setPage(1);
    setApplied(next);
    syncUrl(next);
  };

  const outcomeTabs = useMemo(
    () =>
      [
        {
          id: "all" as const,
          label: t("project_opportunity_history_all", { defaultValue: "All" }),
          count: counts.all,
        },
        {
          id: "WON" as const,
          label: t("project_outcome_won", { defaultValue: "Won" }),
          count: counts.WON,
        },
        {
          id: "LOST" as const,
          label: t("project_outcome_lost", { defaultValue: "Lost" }),
          count: counts.LOST,
        },
        {
          id: "DELETED" as const,
          label: t("project_outcome_deleted", { defaultValue: "Deleted" }),
          count: counts.DELETED,
        },
      ] as const,
    [counts, t]
  );

  const setOutcomeTab = (id: OutcomeFilter) => {
    setOutcome(id);
    const next = { ...applied, outcome: id };
    setPage(1);
    setApplied(next);
    syncUrl(next);
  };

  const selectedBoardName = useMemo(() => {
    if (!boardId) return "";
    return boards.find((b) => b.id === boardId)?.name || "";
  }, [boards, boardId]);

  const onSelectBoard = (id: string) => {
    setBoardId(id);
    const next = { ...applied, boardId: id };
    setPage(1);
    setApplied(next);
    syncUrl(next);
  };

  return (
    <Page
      fullWidth
      title={t("project_opportunity_history_title", {
        defaultValue: "Opportunity history",
      })}
      backAction={{
        content: t("project-boards", { defaultValue: "Boards" }),
        url: boardId
          ? `/app/${companyId}/project-boards/${boardId}`
          : `/app/${companyId}/project-boards`,
      }}
      subtitle={t("project_opportunity_history_subtitle", {
        defaultValue:
          "Closed deals (Won, Lost, Deleted) filtered by period.",
      })}
      titleMetadata={
        companyId ? (
          <ProjectBoardSwitcher
            companyId={companyId}
            boardId={boardId}
            boardName={selectedBoardName}
            onSelectBoard={onSelectBoard}
            allowAllBoards
          />
        ) : null
      }
    >
      <BlockStack gap="400">
        <Card>
          <form onSubmit={applyFilters} className="space-y-4 p-1">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[16rem]">
                <DateRangePicker
                  start={dateRange.start}
                  end={dateRange.end}
                  label={t("date_range", { defaultValue: "Date range" })}
                  onChange={(r) =>
                    setDateRange({ start: r.start, end: r.end })
                  }
                />
              </div>
              <button
                type="submit"
                className="rounded-md bg-[var(--p-color-bg-fill-brand)] px-4 py-2 text-sm font-semibold text-[var(--p-color-text-brand-on-bg-fill)] max-md:min-h-11"
              >
                {t("apply", { defaultValue: "Apply" })}
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-md border border-[var(--p-color-border)] px-4 py-2 text-sm font-semibold text-[var(--p-color-text)] max-md:min-h-11"
              >
                {t("clear", { defaultValue: "Clear" })}
              </button>
            </div>
          </form>
        </Card>

        <Card padding="0">
          <div className="flex gap-1 overflow-x-auto border-b border-[var(--p-color-border)] px-3 py-2">
            {outcomeTabs.map((tab) => {
              const selected = applied.outcome === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setOutcomeTab(tab.id)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold max-md:min-h-11 ${
                    selected
                      ? "bg-[var(--p-color-bg-fill-brand)] text-white"
                      : "bg-[var(--p-color-bg-surface-secondary)] text-[var(--p-color-text-secondary)]"
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--p-color-text-secondary)]">
              {t("loading", { defaultValue: "Loading…" })}
            </div>
          ) : entries.length === 0 ? (
            <div className="p-10 text-center">
              <Text as="p" tone="subdued">
                {t("project_opportunity_history_empty", {
                  defaultValue:
                    "No closed opportunities in this period.",
                })}
              </Text>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[56rem] text-left text-sm">
                <thead className="border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface-secondary)] text-xs uppercase tracking-wide text-[var(--p-color-text-secondary)]">
                  <tr>
                    {/* The name column stays in view while a phone scrolls the table sideways. */}
                    <th className={`${STICKY_NAME_CELL} bg-[var(--p-color-bg-surface-secondary)] px-4 py-2.5 font-semibold`}>
                      {t("name", { defaultValue: "Name" })}
                    </th>
                    <th className="px-4 py-2.5 font-semibold">
                      {t("outcome", { defaultValue: "Outcome" })}
                    </th>
                    <th className="px-4 py-2.5 font-semibold">
                      {t("project_opportunity_closed_at", {
                        defaultValue: "Closed at",
                      })}
                    </th>
                    <th className="px-4 py-2.5 font-semibold">
                      {t("value", { defaultValue: "Value" })}
                    </th>
                    <th className="px-4 py-2.5 font-semibold">
                      {t("project-board", { defaultValue: "Board" })}
                    </th>
                    <th className="px-4 py-2.5 font-semibold">
                      {t("assignee", { defaultValue: "Assignee" })}
                    </th>
                    <th className="px-4 py-2.5 font-semibold">
                      {t("customer", { defaultValue: "Customer" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((row) => {
                    const detailUrl =
                      row.boardId &&
                      `/app/${companyId}/project-boards/${row.boardId}/project/${row.id}`;
                    return (
                      <tr
                        key={row.id}
                        className="group border-b border-[var(--p-color-border)] last:border-b-0 hover:bg-[var(--p-color-bg-surface-hover)]"
                      >
                        <td className={`${STICKY_NAME_CELL} bg-[var(--p-color-bg-surface)] px-4 py-3 group-hover:bg-[var(--p-color-bg-surface-hover)]`}>
                          {detailUrl ? (
                            <Link
                              to={detailUrl}
                              className="font-semibold text-[var(--p-color-text-link)] hover:underline"
                            >
                              {row.name}
                            </Link>
                          ) : (
                            <span className="font-semibold">{row.name}</span>
                          )}
                          {row.contactName && (
                            <div className="text-xs text-[var(--p-color-text-secondary)]">
                              {row.contactName}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={outcomeBadgeTone(row.outcome)}>
                            {row.outcome
                              ? t(
                                  `project_outcome_${row.outcome.toLowerCase()}`,
                                  { defaultValue: row.outcome }
                                )
                              : "—"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[var(--p-color-text-secondary)]">
                          {row.outcomeAt
                            ? formatPrettyDate(row.outcomeAt, "lt-LT", true)
                            : "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatEur(row.valueEur)}
                        </td>
                        <td className="px-4 py-3">
                          <div>{row.boardName || "—"}</div>
                          {row.columnName && (
                            <div className="text-xs text-[var(--p-color-text-secondary)]">
                              {row.columnName}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.user?.name || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {row.customer?.name || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 border-t border-[var(--p-color-border)] px-4 py-3 text-sm md:justify-end md:gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-[var(--p-color-border)] px-3 py-1.5 disabled:opacity-40 max-md:min-h-11 max-md:min-w-11"
              >
                ←
              </button>
              <span className="text-[var(--p-color-text-secondary)]">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md border border-[var(--p-color-border)] px-3 py-1.5 disabled:opacity-40 max-md:min-h-11 max-md:min-w-11"
              >
                →
              </button>
            </div>
          )}
        </Card>
      </BlockStack>
    </Page>
  );
}
